import { authReturnPath } from "@lib/authReturnPath";
import { useEffect, useState } from "react";
import Dexie from "dexie";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@sfc/ui";
import { Link } from "wouter";
import { Page } from "@client/components/utils/Page";
import { clearAuthToken } from "@client/iphone/lib/auth";
import { clearImpersonationStateForSignOut } from "@client/ImpersonateContext";
import { PROPOSAL_SEARCH_INDEX_DB_NAME } from "@client/hooks/proposal-search/proposalSearchTypes";
import { SEARCH_INDEX_DB_NAME } from "@client/pages/round/funder-workspace/search/searchTypes";
import { deletePersistedQueryCache } from "@client/utils/queryPersister";
import { clearOfflineCache } from "@client/offline/offlineCache";
import { purgeUserDataDatabases } from "@client/pages/signout/purgeUserData";
import {
  describePendingEdits,
  describeRoundsToReopen,
  readPendingOfflineEdits,
  type PendingOfflineEdits,
} from "@client/pages/signout/pendingOfflineEdits";
import {
  abandonLockdownPersistence,
  isLockdownPersistenceActive,
  suspendLockdownPersistence,
} from "@client/offline/lockdownPersistenceControls";
import {
  flushArtifactCacheWrites,
  suspendArtifactCacheWrites,
} from "@client/offline/queryClientPersister";
import {
  flushAggregateCacheWrites,
  suspendAggregateCacheWrites,
} from "@engine/storageAdapters/roundAggregateCache";

// Drop everything we persist to IndexedDB: the funder-search and
// proposals-search indexes, the persisted corpus query cache, ClientInfoStore's
// proposal field responses, and
// the iPad offline caches — the SPAStorage DB (cached round aggregates +
// unconfirmed actions) and the ArtifactQueryCache. Without the last two, a user
// who signs out while online could relaunch offline and be served the previous
// user's cached round / identity / artifact data on a shared device.
// Best-effort — `Dexie.delete` waits while another open tab holds a connection,
// so cap the wait rather than let it block the actual sign-out.
async function clearIndexedDbState() {
  await Promise.race([
    Promise.allSettled([
      Dexie.delete(SEARCH_INDEX_DB_NAME),
      Dexie.delete(PROPOSAL_SEARCH_INDEX_DB_NAME),
      deletePersistedQueryCache(),
      Dexie.delete("ClientInfoStore"),
      Dexie.delete("SPAStorage"),
      Dexie.delete("ArtifactQueryCache"),
    ]),
    new Promise((resolve) => setTimeout(resolve, 2000)),
  ]);
}

export function SignOut() {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  // null = still reading. Sign-out stays available throughout; the queue only
  // decides how loudly we warn (SP-2583).
  const [pending, setPending] = useState<PendingOfflineEdits | null>(null);

  useEffect(() => {
    let active = true;
    void readPendingOfflineEdits().then((result) => {
      if (active) {
        setPending(result);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  // At risk online or offline alike: the queue drains over the round's socket,
  // and reaching this page already disconnected it. Nothing syncs from here.
  const atRiskEdits = pending?.edits ?? 0;
  // Couldn't count it. Treated as work at risk: an unreadable queue may be a
  // full one, and only the funder can judge that.
  const countUnavailable = pending?.unreadable ?? false;
  const willDiscardEdits = atRiskEdits > 0 || countUnavailable;

  async function handleSignOut() {
    // Read from the render that produced the button, so it is what the funder
    // actually saw.
    const warningWasOnScreen = willDiscardEdits;
    setIsSigningOut(true);
    setPurgeError(null);

    // Re-read on every destructive press: the render-time value goes stale
    // when the first read is still in flight, or when an edit is queued after
    // it. A stale zero would purge unsynced work with no warning.
    const atRisk = await readPendingOfflineEdits();
    setPending(atRisk);

    // Work at risk that the funder was never shown: warn instead of deciding
    // for them.
    if (!warningWasOnScreen && (atRisk.edits > 0 || atRisk.unreadable)) {
      setIsSigningOut(false);
      return;
    }

    // No sync attempt here, deliberately: this route already disconnected the
    // round, so waiting would stall and then warn anyway.
    // Stop the non-blocking cache writers FIRST, so a throttled/in-flight
    // persist can't recreate an IndexedDB cache after we delete it below (which
    // would leave the previous user's offline data for the next session).
    suspendArtifactCacheWrites();
    suspendAggregateCacheWrites();
    clearImpersonationStateForSignOut();
    clearOfflineCache();
    // Drain any write that already passed the suspend guard and is mid-flight in
    // its IndexedDB transaction, so it can't complete *after* the deletes below
    // and recreate the DB with the previous user's offline data.
    await Promise.allSettled([
      flushArtifactCacheWrites(),
      flushAggregateCacheWrites(),
      // Under iOS Lockdown Mode the databases below are an in-memory stand-in
      // whose durable copy lives in native files. Stop writing them and drain
      // what is in flight; deleting the databases then deletes the files too,
      // so the next cold start cannot restore the previous user's round.
      suspendLockdownPersistence(),
    ]);

    if (isLockdownPersistenceActive()) {
      // The durable copy is a native file, and the wrapper already retried.
      // Signing out is a promise to remove it; if we cannot, say so instead of
      // redirecting and leaving the round behind.
      try {
        await purgeUserDataDatabases();
      } catch (error) {
        console.error("[SP-2363] sign-out purge failed:", error);
        // The in-memory databases are already deleted and the files partially
        // gone. If the user carries on in this tab instead of reloading, make
        // any further offline edit FAIL rather than complete in memory and
        // vanish on force-quit — the reload below is still the real fix.
        abandonLockdownPersistence();
        // Do NOT try to carry on in this tab. Getting here means the in-memory
        // databases are already deleted, their hydration markers are stale, and
        // three modules have latched permanent "stop writing" flags. Restoring
        // that by hand is how this path grew its bugs; a reload rebuilds all of
        // it from the native files that are still on disk. Until the user
        // reloads, the page offers nothing else to do.
        setPurgeError(
          "Could not remove your offline round data from this device, so you have not been signed out. Reload and try again.",
        );
        setIsSigningOut(false);
        return;
      }
    }

    await clearIndexedDbState();
    // Native builds authenticate with a bearer token in localStorage, not the
    // Auth.js cookie — clear it so sign-out actually signs the user out.
    clearAuthToken();
    const returnTo = authReturnPath(
      new URLSearchParams(window.location.search).get("returnTo"),
    );

    try {
      // Get CSRF token from Auth.js
      const csrfRes = await fetch("/auth/csrf");
      const { csrfToken } = await csrfRes.json();

      // POST to Auth.js signout endpoint
      const form = new URLSearchParams();
      form.append("csrfToken", csrfToken);
      form.append("callbackUrl", returnTo);

      const res = await fetch("/auth/signout", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: form.toString(),
        redirect: "follow",
      });

      // Auth.js returns a redirect — follow it or go home
      if (returnTo !== "/") {
        window.location.href = returnTo;
      } else if (res.redirected) {
        window.location.href = res.url;
      } else {
        window.location.href = "/";
      }
    } catch {
      // Native bundles have no same-origin Auth.js endpoint. Local credentials
      // are already cleared; preserve the validated invitation destination.
      window.location.href = returnTo;
    }
  }

  return (
    <Page title="Sign out">
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle>
            {countUnavailable
              ? "Sign out without checking for unsynced changes?"
              : willDiscardEdits
                ? `Sign out and lose ${describePendingEdits(atRiskEdits)}?`
                : "Are you sure you want to sign out?"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {countUnavailable ? (
              <>
                This iPad couldn't read the changes you made offline, so we
                can't tell whether any are still waiting to reach the server.
                Signing out deletes any that are. Open the round while connected
                to the internet to sync them.
              </>
            ) : willDiscardEdits ? (
              <>
                {describePendingEdits(atRiskEdits)} you made offline{" "}
                {atRiskEdits === 1 ? "is" : "are"} saved only on this iPad and{" "}
                {atRiskEdits === 1 ? "hasn't" : "haven't"} reached the server
                yet. Signing out deletes {atRiskEdits === 1 ? "it" : "them"}.
                Open{" "}
                {pending != null
                  ? describeRoundsToReopen(pending)
                  : "the round"}{" "}
                while connected to the internet to sync{" "}
                {atRiskEdits === 1 ? "it" : "them"}.
              </>
            ) : (
              "You will need to sign in again to access your account."
            )}
          </p>
          {purgeError != null ? (
            <p className="mt-3 text-sm text-destructive">{purgeError}</p>
          ) : null}
        </CardContent>
        <CardFooter className="flex gap-3">
          {purgeError != null ? (
            // The only safe way out of a half-completed sign-out.
            <Button onClick={() => window.location.reload()}>Reload</Button>
          ) : willDiscardEdits ? (
            // Safe choice primary; discarding stays reachable but plain, and
            // names what it destroys.
            <>
              <Button nativeButton={false} render={<Link href="/" />}>
                Keep working
              </Button>
              <Button
                variant="ghost"
                onClick={handleSignOut}
                disabled={isSigningOut}
              >
                {isSigningOut ? "Signing out…" : "Sign out and discard"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleSignOut} disabled={isSigningOut}>
                {isSigningOut ? "Signing out…" : "Sign out"}
              </Button>
              <Button
                variant="outline"
                nativeButton={false}
                render={<Link href="/" />}
              >
                Cancel
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </Page>
  );
}
